import React, { useState, useCallback, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import classes from './IdeaMap.module.css';
import HUD from './HUD/HUD';
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useConfirmExit } from "./useConfirmExit";

const IdeaMap = ({ user }) => {
    const location = useLocation();
      const initialGraph = location.state?.graphData;

  const [graphData, setGraphData] = useState(initialGraph || null);
const [answeredCount, setAnsweredCount] = useState(0);


useEffect(() => {
  if (initialGraph) return; // if user loaded from saved mindmap, skip daily

  const unsub = onSnapshot(doc(db, "dailyQuestion", "current"), (docSnap) => {
    if (docSnap.exists()) {
      const q = docSnap.data().question;

      setGraphData({
        nodes: [
          { id: "root", question: q, answer: "", x: 0, y: 0, level: 0 }
        ],
        links: []
      });
    }
  });

  return () => unsub();
}, [initialGraph]);

useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (answeredCount > 0) {
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    }
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, [answeredCount]);


  const [selectedNode, setSelectedNode] = useState(null);
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fgRef = useRef();

  const [hoverNode, setHoverNode] = useState(null);
useConfirmExit(
    answeredCount > 0,
    "You have answered questions. Do you really want to leave?"
  );


  // Generate random questions using ChatGPT API
  const generateQuestions = async (originalQuestion, answer) => {
    setIsLoading(true);
    try {
      // For development, we'll use a mock API or you can set up a local server
      // In production, this would point to your Firebase function
    const response = await fetch('https://generatequestions-5tsg3pvfba-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originalQuestion,
          answer
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate questions');
      }

      const data = await response.json();
      return data.questions;
    } catch (error) {
      console.error('Error generating questions:', error);
      // Fallback to some default questions if API fails
      return [
        'How does this relate to your personal experiences?',
        'What are the broader implications of this idea?',
        'What questions does this raise for you?',
        'How might this concept apply in different contexts?',
        'What would happen if we took this idea to its logical conclusion?'
      ];
    } finally {
      setIsLoading(false);
    }
  };

  const handleNodeClick = useCallback((node) => {
      if (node.answer && node.answer.trim()) {
                  fgRef.current.zoom(1.5, 1000);
              fgRef.current.centerAt(node.x, node.y, 1000); // smooth pan (1s)
              setSelectedNode(null);
              setAnswer('');
        return;
      }
      setSelectedNode(node);
      setAnswer(node.answer || '');
      fgRef.current.centerAt(node.x, node.y, 1000); // smooth pan (1s)
          fgRef.current.zoom(1.5, 1000);
  }, []);

  const handleSubmitAnswer = async () => {
    if (!answer.trim() || !selectedNode) return;

    // Update the selected node with the answer
    const updatedNodes = graphData.nodes.map(node => {
  if (node.id === selectedNode.id) {
    if (!node.answer) {
      return {
        ...node,
        answer: answer.trim(),
        answeredIndex: answeredCount
      };
    }
    return { ...node, answer: answer.trim() };
  }
  return node;
});

// If this was a first-time answer, bump the counter
if (!selectedNode.answer) {
  setAnsweredCount(prev => prev + 1);
}
    // Generate new questions
    let newQuestions = await generateQuestions(selectedNode.question, answer.trim());
newQuestions = newQuestions.map(q => q.replace(/^\s*\d+[.)]\s*/, ''));
    // Create new nodes for the generated questions
// Create new nodes for the generated questions
const baseRadius = 200; // base edge length
const newLevel = selectedNode.level + 1;
const fanAngle = Math.PI / 1.5; // ~30° cone spread

const newNodes = newQuestions.map((question, index) => {
  const parentAngle =
    selectedNode.angle !== undefined
      ? selectedNode.angle
      : (2 * Math.PI * index) / newQuestions.length;

  const childAngle =
    parentAngle -
    fanAngle / 2 +
    (index * fanAngle) / (newQuestions.length - 1 || 1);

  // 🔑 scale edge length by depth
  const edgeLength = baseRadius + newLevel * 50;

  return {
    id: `${selectedNode.id}_${Date.now()}_${index}`,
    question,
    answer: '',
    level: newLevel,
    angle: childAngle,
    x: selectedNode.x + edgeLength * Math.cos(childAngle),
    y: selectedNode.y + edgeLength * Math.sin(childAngle),
  };
});



    // Create links from the selected node to new nodes
    const newLinks = newNodes.map(node => ({
      source: selectedNode.id,
      target: node.id
    }));

    // Update graph data
    setGraphData({
      nodes: [...updatedNodes, ...newNodes],
      links: [...graphData.links, ...newLinks]
    });

    setTimeout(() => {
  if (fgRef.current) {
    fgRef.current.d3ReheatSimulation();

    // ✅ Pan to the answered node without changing zoom
    fgRef.current.centerAt(selectedNode.x, selectedNode.y, 1000); // smooth pan (1s)
  }
}, 0);

// console.log(graphData);


    // Close modal and reset state
    // setIsModalOpen(false);
    setSelectedNode(null);
    setAnswer('');
  };

  // const closeModal = () => {
  //   // setIsModalOpen(false);
  //   setSelectedNode(null);
  //   setAnswer('');
  // };

const nodeCanvasObject = useCallback((node, ctx) => {
  const fontSize = 12;
  ctx.font = `${fontSize}px Sans-Serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const wrapText = (text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    for (let i = 0; i < words.length; i++) {
      const testLine = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = words[i];
      } else {
        line = testLine;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  const maxWidth = 300;
  const questionLines = wrapText(node.question, maxWidth);
  const answerLines = node.answer ? wrapText(node.answer, maxWidth) : [];
  const lineHeight = fontSize * 1.4;
  const totalHeight = (questionLines.length + answerLines.length) * lineHeight + 8;

  const rectX = node.x - maxWidth / 2 - 4;
  const rectY = node.y - totalHeight / 2;
  const rectW = maxWidth + 16;
  const rectH = totalHeight + 8;
  const radius = 10; // 🔑 border radius

  // helper: rounded rectangle path
  const drawRoundedRect = (x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  // background fill
  if (node.answer && node.answeredIndex !== undefined) {
    const totalSteps = Math.max(answeredCount, 1);
    const startColor = [0, 150, 255];   // dark blue
    const endColor   = [144, 202, 249]; // light blue

    const slowFactor = 3;
    const tRaw = (node.answeredIndex / (totalSteps - 1 || 1)) / slowFactor;
    const t = Math.min(1, tRaw);

    const r = Math.round(startColor[0] + t * (endColor[0] - startColor[0]));
    const g = Math.round(startColor[1] + t * (endColor[1] - startColor[1]));
    const b = Math.round(startColor[2] + t * (endColor[2] - startColor[2]));

    const baseColor = `rgb(${r},${g},${b})`;

    const gradient = ctx.createLinearGradient(rectX, rectY, rectX + rectW, rectY + rectH);
    const lighten = (c, amt) =>
      `rgb(${Math.min(255, c[0] + amt)}, ${Math.min(255, c[1] + amt)}, ${Math.min(255, c[2] + amt)})`;
    const lightColor = lighten([r, g, b], 60);

    gradient.addColorStop(0, lightColor);
    gradient.addColorStop(1, baseColor);

    ctx.fillStyle = gradient;
  } else {
    ctx.fillStyle = 'rgba(215, 211, 211, 0.85)';
  }

  // draw rounded box
  drawRoundedRect(rectX, rectY, rectW, rectH, radius);
  ctx.fill();

  ctx.strokeStyle = '#0e0e0eff';
    ctx.lineWidth = 2;
    drawRoundedRect(rectX, rectY, rectW, rectH, radius);
    ctx.stroke();

  // hover border
  if (hoverNode && hoverNode.id === node.id) {
    ctx.strokeStyle = '#2196F3';
    ctx.lineWidth = 2;
    drawRoundedRect(rectX, rectY, rectW, rectH, radius);
    ctx.stroke();
  }

  if (selectedNode && selectedNode.id === node.id) {
  ctx.strokeStyle = '#7c7973ff';   // orange border
  ctx.lineWidth = 3;
  drawRoundedRect(rectX, rectY, rectW, rectH, radius);
  ctx.stroke();

  // optional glow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
  ctx.shadowBlur = 12;
  ctx.stroke();
  ctx.shadowBlur = 0;
}


  // text
  // text
if (node.answer) {
  // Question = bold, black, left-aligned
  ctx.font = `bold ${fontSize}px Sans-Serif`;
  ctx.fillStyle = '#000000'; 
  ctx.textAlign = 'left';

  questionLines.forEach((line, i) => {
    ctx.fillText(line, rectX + 8, rectY + (i + 1) * lineHeight); // left padding
  });

  // Answer = normal, left-aligned, softer color
  ctx.font = `${fontSize}px Sans-Serif`;
  ctx.fillStyle = '#1d1d1dff'; 
  ctx.textAlign = 'right';

  answerLines.forEach((line, i) => {
    ctx.fillText(
      line,
      rectX + rectW - 8,
      rectY + (questionLines.length + i + 1) * lineHeight
    );
  });
} else {
  // unanswered nodes stay centered + blue
  ctx.font = `${fontSize}px Sans-Serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#2c2d2dff';

  questionLines.forEach((line, i) => {
    ctx.fillText(line, node.x, rectY + (i + 1) * lineHeight);
  });

  if (answerLines.length) {
    ctx.fillStyle = '#4CAF50';
    answerLines.forEach((line, i) => {
      ctx.fillText(line, node.x, rectY + (questionLines.length + i + 1) * lineHeight);
    });
  }
}

}, [hoverNode, answeredCount, selectedNode]);

const nodePointerAreaPaint = useCallback((node, color, ctx) => {
  const fontSize = 12;
  ctx.font = `${fontSize}px Sans-Serif`;

  const wrapText = (text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    for (let i = 0; i < words.length; i++) {
      const testLine = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = words[i];
      } else {
        line = testLine;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  const maxWidth = 300;
  const questionLines = wrapText(node.question, maxWidth);
  const answerLines = node.answer ? wrapText(node.answer, maxWidth) : [];
  const lineHeight = fontSize * 1.4;
  const totalHeight = (questionLines.length + answerLines.length) * lineHeight + 8;

  ctx.fillStyle = color;
  ctx.fillRect(
    node.x - maxWidth / 2 - 4,
    node.y - totalHeight / 2,
    maxWidth + 8,
    totalHeight
  );
}, []);


if (!graphData) {
  return (<div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "1rem",
      }}
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          background: `
            radial-gradient(circle at 30% 30%, 
              rgba(255,255,255,0.9), 
              rgba(0,112,243,0.8) 40%, 
              rgba(0,0,0,0.25) 100%)`,
        }}
      />
      <p style={{ fontSize: "1rem", color: "#555", fontWeight: 500 }}>
        ... Loading ...
      </p>
    </div>);
}

  

  return (
    <div className={classes.ideaMapContainer}>
      {/* <div className={classes.header}>
        <h1>Idea Map</h1>
        <p>Click on any node to explore ideas and generate new questions</p>
      </div> */}
      
      <div className={classes.graphContainer}>
       <ForceGraph2D
  ref={fgRef}
  graphData={graphData}
  onNodeClick={handleNodeClick}
  onNodeHover={node => {
    setHoverNode(node || null);
    document.body.style.cursor = node ? 'pointer' : 'default'; // 👈 makes cursor change
  }}
  nodeCanvasObject={nodeCanvasObject}
  nodePointerAreaPaint={nodePointerAreaPaint}
  linkWidth={2}
  linkColor="#999"
  backgroundColor="#f8f9fa"
   width={window.innerWidth}
  height={window.innerHeight}
  cooldownTicks={0}
  panInteraction={true}          // ✅ keep panning
  zoomInteraction={false}        // ❌ disable zoom
  dragNodes={true}               // ✅ allow dragging nodes
  enableNodeDrag={true}          // ✅ enable node drag
    d3VelocityDecay={1}     // freeze forces
/>

<HUD
        answeredCount={answeredCount}
        totalNodes={graphData.nodes.length}
        graphData={graphData}
        fgRef={fgRef}
        user={user}
      />

      </div>

        
{selectedNode && (<div className={`${classes.replyBar} ${selectedNode ? classes.show : classes.hide}`}>
  <motion.div
    className={classes.replyInner}
    animate={{
          // x: isLoading ? "0%" : 0,   // moves its full width
      width: isLoading ? "44px" : "100%",   // shrink to just the icon
    }}
    transition={{ duration: 0.8, ease: "easeInOut" }}
    style={{ overflow: "hidden", 
      padding: isLoading ? "0.25rem" : "0.75rem",
      borderRadius: isLoading ? "0px" : "15px",
      border: isLoading ? "none" : "1px solid #ccc",
      background: isLoading ? "linear-gradient(to right, #e0e0e0, #ffffff)": "white"
    }}
  >
    {!isLoading ? (
      <>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={`Answer: ${selectedNode.question}`}
          className={classes.replyInput}
          rows={2}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (answer.trim() && !isLoading) {
                handleSubmitAnswer();
              }
            }
          }}
        />
        <button
          className={classes.sendButton}
          onClick={handleSubmitAnswer}
          disabled={!answer.trim() || isLoading}
        >
          ➤
        </button>
      </>
    ) : (
      <motion.div
  animate={{ rotate: 360 }}
  transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
  style={{
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: `
      radial-gradient(circle at 30% 30%, 
        rgba(255,255,255,0.9), 
        rgba(0,112,243,0.8) 40%, 
        rgba(0,0,0,0.25) 100%)`,
    
  }}
/>

    )}
  </motion.div>
</div>
)}



    </div>
  );
};

export default IdeaMap;
